import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { X } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";

const DIETS = ["vegetarian", "non-vegetarian", "eggitarian", "vegan"];
const SPICES = ["low", "medium", "high"];
const GENDERS = ["male", "female", "non-binary", "prefer-not-to-say"];

function TagInput({ label, values, onChange, placeholder, testId }) {
  const [v, setV] = useState("");
  const add = () => {
    if (v.trim() && !values.includes(v.trim())) {
      onChange([...values, v.trim()]);
      setV("");
    }
  };
  return (
    <div className="space-y-2">
      <Label className="overline">{label}</Label>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} data-testid={`${testId}-input`} />
        <Button type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>Add</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((x) => (
          <Badge key={x} variant="secondary" className="rounded-full pr-1">
            {x}
            <button type="button" onClick={() => onChange(values.filter((y) => y !== x))} className="ml-1 hover:text-destructive">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: "", age: "", gender: "", location: "",
    diet_type: "", spice_preference: "",
    favorite_cuisines: [], favorite_foods: [], allergies: [], food_restrictions: [],
  });
  const [dna, setDna] = useState(null);
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      const p = r.data.profile || {};
      setForm({
        name: r.data.name || "",
        age: p.age || "",
        gender: p.gender || "",
        location: p.location || "",
        diet_type: p.diet_type || "",
        spice_preference: p.spice_preference || "",
        favorite_cuisines: p.favorite_cuisines || [],
        favorite_foods: p.favorite_foods || [],
        allergies: p.allergies || [],
        food_restrictions: p.food_restrictions || [],
      });
    });
    api.get("/taste-dna").then((r) => setDna(r.data));
    api.get("/permissions").then((r) => setPerms(r.data)).catch(() => {});
  }, []);

  const togglePerm = async (key) => {
    if (!perms) return;
    const next = { ...perms, [key]: !perms[key] };
    setPerms(next);
    try {
      await api.patch("/permissions", { [key]: next[key] });
      toast.success(`${key.replace(/_/g," ")} ${next[key] ? "enabled" : "disabled"}`);
    } catch {
      setPerms(perms); // rollback
      toast.error("Couldn't update permission");
    }
  };

  const dnaOverride = async (dimension, attribute, action) => {
    try {
      await api.patch("/dna/override", { dimension, attribute, action });
      const r = await api.get("/taste-dna");
      setDna(r.data);
      toast.success("Taste DNA updated");
    } catch {
      toast.error("Couldn't update Taste DNA");
    }
  };

  const refreshDna = async () => {
    try {
      await api.post("/dna/refresh");
      const r = await api.get("/taste-dna");
      setDna(r.data);
      toast.success("Taste DNA refreshed");
    } catch {
      toast.error("Refresh failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.age = payload.age ? Number(payload.age) : null;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.put("/profile", payload);
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error("Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  const radarData = dna
    ? [
        { metric: "Spice", value: dna.avg_spice * 20 },
        { metric: "Sweet", value: dna.avg_sweetness * 20 },
        { metric: "Comfort", value: dna.avg_comfort },
        { metric: "Liked", value: Math.min(100, dna.liked_count * 10) },
        { metric: "Variety", value: Math.min(100, (dna.cuisine_breakdown?.length || 0) * 15) },
      ]
    : [];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="profile-page">
      <div className="overline mb-3">Your YUMO</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight">Profile & Taste DNA</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <Card className="lg:col-span-2 p-7 rounded-2xl border-border shadow-none">
          <form onSubmit={save} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
              <div><Label>Age</Label><Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="profile-age" /></div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="profile-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g} className="capitalize">{g.replace(/-/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="profile-location" /></div>
              <div>
                <Label>Diet</Label>
                <Select value={form.diet_type} onValueChange={(v) => setForm({ ...form, diet_type: v })}>
                  <SelectTrigger data-testid="profile-diet"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{DIETS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Spice Preference</Label>
                <Select value={form.spice_preference} onValueChange={(v) => setForm({ ...form, spice_preference: v })}>
                  <SelectTrigger data-testid="profile-spice"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{SPICES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <TagInput label="Favorite Cuisines" testId="cuisines" values={form.favorite_cuisines} onChange={(v) => setForm({ ...form, favorite_cuisines: v })} placeholder="e.g. Indian" />
            <TagInput label="Favorite Foods" testId="foods" values={form.favorite_foods} onChange={(v) => setForm({ ...form, favorite_foods: v })} placeholder="e.g. Biryani" />
            <TagInput label="Allergies" testId="allergies" values={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} placeholder="e.g. peanuts" />
            <TagInput label="Food Restrictions" testId="restrictions" values={form.food_restrictions} onChange={(v) => setForm({ ...form, food_restrictions: v })} placeholder="e.g. no pork" />
            <Button type="submit" disabled={saving} className="rounded-full" data-testid="profile-save">{saving ? "Saving…" : "Save profile"}</Button>
          </form>
        </Card>

        <Card className="p-7 rounded-2xl border-border shadow-none" data-testid="taste-dna-card">
          <div className="overline mb-2">Taste DNA</div>
          <h3 className="font-serif text-2xl font-semibold mb-3">Your flavor fingerprint</h3>
          {dna?.native_state && (
            <div className="text-sm text-muted-foreground mb-3">From <span className="font-medium text-foreground">{dna.native_state}</span></div>
          )}
          <div className="flex gap-6 text-sm">
            <div><div className="font-serif text-3xl font-semibold">{dna?.liked_count ?? 0}</div><div className="text-muted-foreground">liked</div></div>
            <div><div className="font-serif text-3xl font-semibold">{dna?.rejected_count ?? 0}</div><div className="text-muted-foreground">rejected</div></div>
          </div>

          {dna?.summary?.length > 0 && (
            <div className="mt-5 p-4 bg-primary/[0.05] border-l-2 border-primary rounded-r-lg" data-testid="dna-summary">
              <div className="overline mb-2 text-primary">You enjoy</div>
              <ul className="text-sm space-y-1">
                {dna.summary.map((s, i) => (
                  <li key={`${s}-${i}`} className="flex gap-2"><span className="text-primary">✓</span><span>{s}</span></li>
                ))}
              </ul>
            </div>
          )}

          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                <Radar dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          {dna?.cuisine_breakdown?.length > 0 && (
            <div className="mt-3" data-testid="top-cuisines">
              <div className="overline mb-2">Top cuisines</div>
              <div className="flex flex-wrap gap-1.5">
                {dna.cuisine_breakdown.slice(0, 6).map((c) => (
                  <Badge key={c.cuisine} variant="outline" className="rounded-full">
                    {c.cuisine} <span className="ml-1 text-muted-foreground">×{c.count}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {dna?.spice_affinity && Object.keys(dna.spice_affinity).length > 0 && (
            <div className="mt-4" data-testid="spice-affinity">
              <div className="overline mb-2">Spice affinity</div>
              <div className="flex items-end gap-1.5 h-12">
                {[1,2,3,4,5].map((lvl) => {
                  const v = dna.spice_affinity[String(lvl)] || 0;
                  const h = Math.min(100, Math.max(8, Math.abs(v) * 18));
                  return (
                    <div key={lvl} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-full rounded-t ${v >= 0 ? "bg-primary" : "bg-muted-foreground/30"}`} style={{ height: `${h}%` }} />
                      <div className="text-[10px] text-muted-foreground tabular-nums">{lvl}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {dna?.recent_likes?.length > 0 && (
            <div className="mt-4" data-testid="recent-likes">
              <div className="overline mb-2">Recent likes</div>
              <ul className="text-sm space-y-1">
                {dna.recent_likes.slice(0, 5).map((r, i) => (
                  <li key={`${r.name}-${i}`} className="flex justify-between gap-2">
                    <span className="truncate">{r.name}</span>
                    <span className="text-muted-foreground text-xs shrink-0">{r.cuisine}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dna?.mood_food_pairs?.length > 0 && (
            <div className="mt-4" data-testid="mood-pairs">
              <div className="overline mb-2">Mood ↔ Food associations</div>
              <ul className="text-sm space-y-1 text-foreground/85">
                {dna.mood_food_pairs.slice(0, 4).map((p, i) => (
                  <li key={`${p.mood}-${p.cuisine}-${i}`}><span className="capitalize">{p.mood}</span> → <span className="font-medium">{p.cuisine}</span></li>
                ))}
              </ul>
            </div>
          )}

          {dna?.weather_food_pairs?.length > 0 && (
            <div className="mt-4" data-testid="weather-pairs">
              <div className="overline mb-2">Weather ↔ Food</div>
              <ul className="text-sm space-y-1 text-foreground/85">
                {dna.weather_food_pairs.slice(0, 4).map((p, i) => (
                  <li key={`${p.weather}-${p.cuisine}-${i}`}><span className="capitalize">{p.weather}</span> → <span className="font-medium">{p.cuisine}</span></li>
                ))}
              </ul>
            </div>
          )}

          {/* v5: Declared vs Observed reconciliation (spec Part 9) */}
          {dna?.conflicts?.length > 0 && (
            <div className="mt-5 p-4 border border-amber-200 bg-amber-50 rounded-lg" data-testid="dna-conflicts">
              <div className="overline mb-2 text-amber-700">Declared vs observed</div>
              <ul className="text-sm space-y-2">
                {dna.conflicts.map((c, i) => (
                  <li key={`${c.dimension}-${i}`} className="text-amber-900">{c.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* v5: Confidence-scored cache with edit controls (spec Parts 11, 18) */}
          {dna?.cache && Object.keys(dna.cache).length > 0 && (
            <div className="mt-5" data-testid="dna-confidence-cache">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Learned preferences (confidence-scored)</div>
                <Button type="button" variant="ghost" size="sm" onClick={refreshDna} data-testid="dna-refresh-btn">Refresh</Button>
              </div>
              {Object.entries(dna.cache).slice(0, 4).map(([dim, rows]) => (
                <div key={dim} className="mt-3">
                  <div className="text-xs uppercase text-muted-foreground mb-1.5">{dim.replace(/_/g," ")}</div>
                  <div className="space-y-1.5">
                    {rows.slice(0, 4).map((r) => (
                      <div key={`${dim}-${r.attribute}`} className="flex items-center gap-2 text-sm" data-testid={`dna-row-${dim}-${r.attribute}`}>
                        <span className="w-24 truncate capitalize">{r.attribute}</span>
                        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className={`h-full ${r.preference === "avoids" ? "bg-destructive/70" : "bg-primary"}`}
                               style={{ width: `${Math.round(r.confidence * 100)}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">{Math.round(r.confidence * 100)}%</span>
                        <button type="button" onClick={() => dnaOverride(dim, r.attribute, "suppress")}
                                className="text-[11px] text-muted-foreground hover:text-destructive"
                                data-testid={`dna-suppress-${dim}-${r.attribute}`}>Not really</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* v5: Permissions panel (spec Parts 4, 19) */}
        <Card className="p-7 rounded-2xl border-border shadow-none md:col-span-2" data-testid="permissions-card">
          <div className="overline mb-2">Privacy & Permissions</div>
          <h3 className="font-serif text-2xl font-semibold mb-1">What YUMO is allowed to learn</h3>
          <p className="text-sm text-muted-foreground mb-5">Granular control. Revoke anytime — YUMO stops using that signal immediately.</p>
          {perms ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { k: "taste_personalization", label: "Taste personalization", desc: "Learn your food preferences from your YUMO activity" },
                { k: "location", label: "Location", desc: "Nearby restaurant discovery" },
                { k: "restaurant_preferences", label: "Restaurant preferences", desc: "Use restaurant interactions to improve recommendations" },
                { k: "food_history_import", label: "Food order history import", desc: "Accept Swiggy/Zomato exports (coming soon)" },
                { k: "grocery_history_import", label: "Grocery history import", desc: "Accept Zepto/Blinkit exports (coming soon)" },
                { k: "health_info", label: "Health info", desc: "Use dietary/health inputs for smarter picks" },
              ].map(({ k, label, desc }) => (
                <label key={k} className="flex items-start gap-3 p-3 border border-border rounded-xl cursor-pointer hover:bg-secondary/40"
                       data-testid={`perm-row-${k}`}>
                  <input type="checkbox" checked={!!perms[k]} onChange={() => togglePerm(k)}
                         className="mt-1 h-4 w-4 accent-primary" data-testid={`perm-toggle-${k}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Loading permissions…</div>
          )}
        </Card>
      </div>
    </div>
  );
}
